import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  HelpCircle, Mail, Users, BookOpenCheck, Receipt, ClipboardCheck, Heart, AlertTriangle,
  Car, Wallet, MessageCircle, Inbox, CreditCard, Sparkles, Stethoscope, Search, ArrowRight,
  CheckCircle2, Lightbulb, Smartphone, Shield,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// =====================================================================
// Topic registry — each topic = { id, title, icon, audience, summary, body }
// `audience`: "client" | "worker" | "all"
// `body`: an array of blocks; supported block types: 'p', 'steps', 'tip', 'warn', 'list'
// =====================================================================
const TOPICS = [
  {
    id: "admin-access", icon: Shield, audience: "all",
    title: "Sign in as admin and manage sponsorships",
    summary: "Admin can sponsor accounts (full app access for free), promote others to admin, and see every user. Use the demo admin or promote your own account.",
    body: [
      { type: "p", text: "Admin is a separate flag on a user account — any role (client or worker) can also be admin. Once you have admin rights, a <b>Shield</b> link appears in your sidebar leading to the Admin page." },
      { type: "steps", title: "Quickest: sign in with the demo admin", steps: [
        "On the login screen, choose the <b>Client</b> tab.",
        "Enter <b>admin@example.com</b> and password <b>Admin123!</b>",
        "Tap <b>Sign in</b>. The Shield 'Admin' link appears in the sidebar.",
      ]},
      { type: "steps", title: "Better: promote your own account", steps: [
        "Sign in as the demo admin (above).",
        "Open <b>Admin</b> in the sidebar.",
        "Search for your own email in the search box.",
        "Click <b>Make admin</b> on your row. Your account now has admin rights.",
        "Sign out, sign back in as yourself — the Shield link will be there.",
      ]},
      { type: "steps", title: "Sponsoring an account", steps: [
        "Open <b>Admin</b>.",
        "Find the account you want to sponsor.",
        "Click <b>Sponsor</b> — they immediately get full app access for free, no time limit.",
        "To revoke later, click <b>Un-sponsor</b> on that same row (it puts them back to normal billing).",
      ]},
      { type: "tip", text: "You can grant or revoke admin rights for any user from the Admin page. The only thing you can't do is revoke your own admin — ask another admin to do it for you." },
      { type: "warn", text: "Sponsored accounts can use timesheets and invoices for free indefinitely. Use this for trial users, family members, or anyone who needs the app gifted to them." },
    ],
  },
  {
    id: "install-app", icon: Smartphone, audience: "all",
    title: "Install the app on your phone or computer",
    summary: "One-tap access from your home screen or desktop — no browser bar, works offline for the login screen, and it remembers you between sessions.",
    body: [
      { type: "p", text: "The hub is a Progressive Web App (PWA), which means you can install it like a real app on any device — for free, without an app store." },
      { type: "steps", title: "iPhone or iPad (Safari)", steps: [
        "Open the hub in <b>Safari</b>.",
        "Tap the <b>Share</b> button (the square with an up-arrow at the bottom of the screen).",
        "Scroll down and tap <b>Add to Home Screen</b>.",
        "Tap <b>Add</b> in the top-right corner. The hub icon now lives on your home screen.",
      ]},
      { type: "steps", title: "Android phone (Chrome)", steps: [
        "Open the hub in <b>Chrome</b>.",
        "When the app loads, tap the <b>Install</b> button in the sidebar (or the install banner Chrome shows automatically).",
        "Confirm <b>Install</b> in the popup. Done — you'll find the hub in your apps drawer and on your home screen.",
      ]},
      { type: "steps", title: "Windows or Mac (Chrome / Edge)", steps: [
        "Open the hub in <b>Chrome</b> or <b>Edge</b>.",
        "Click the <b>Install</b> button in the sidebar — or the small install icon in the right side of the address bar.",
        "Confirm. The hub opens in its own window with no browser tabs, and you can pin it to your taskbar or dock.",
      ]},
      { type: "steps", title: "Mac Safari (macOS Sonoma or newer)", steps: [
        "Open the hub in <b>Safari</b>.",
        "Choose <b>File → Add to Dock</b> from the menu bar.",
        "The hub now opens like a native Mac app from the Dock.",
      ]},
      { type: "tip", text: "Already installed? The Install button auto-hides — you'll only see it when an install is actually possible." },
      { type: "warn", text: "Firefox on desktop doesn't have a built-in install option for PWAs. Chrome, Edge, Safari and Brave all work." },
    ],
  },
  {
    id: "email-setup", icon: Mail, audience: "client",
    title: "Set up your email so the app can send invoices for you",
    summary: "Connect your own email account in Settings → Email. We'll use it to send invoices, incident alerts and PDFs to plan managers, family and workers.",
    body: [
      { type: "p", text: "All emails the app sends — invoices, incident alerts, vital signs PDFs — go out from YOUR email account. This keeps everything personal and trustworthy. You only need to set this up once." },
      { type: "steps", title: "If you use Gmail (most common)", steps: [
        "Go to <b>Settings → Email</b> in the sidebar.",
        "Turn on <b>2-Step Verification</b> for your Google account at <code>myaccount.google.com/security</code>.",
        "Once that's on, create an <b>App password</b> at <code>myaccount.google.com/apppasswords</code>. Pick \"Mail\" and \"Other (NDIS Hub)\". Google will show you a 16-character password — copy it.",
        "Back in NDIS Hub Settings → Email, enter:<br/>• <b>SMTP host</b>: <code>smtp.gmail.com</code><br/>• <b>SMTP port</b>: <code>587</code><br/>• <b>SMTP user</b>: your Gmail address<br/>• <b>SMTP password</b>: paste the 16-character App password<br/>• <b>From email</b>: your Gmail address",
        "Click <b>Save SMTP</b>.",
        "Type any email into the test box on the right and click <b>Send test</b> — if you receive it, you're done!",
      ]},
      { type: "tip", text: "Use the App password, NOT your normal Gmail password. Gmail blocks normal passwords from being used by apps for security." },
      { type: "steps", title: "Other email providers", steps: [
        "Outlook / Hotmail: SMTP host <code>smtp-mail.outlook.com</code>, port <code>587</code>.",
        "Yahoo: SMTP host <code>smtp.mail.yahoo.com</code>, port <code>587</code>.",
        "Your own domain: ask your email provider for the SMTP server settings.",
      ]},
      { type: "p", text: "We never see or store your email password unencrypted — it stays in your private settings and is only used to send your emails." },
    ],
  },
  {
    id: "instant-alerts", icon: Inbox, audience: "client",
    title: "Get instant emails when a worker creates something to approve",
    summary: "When a worker logs a timesheet, vital reading, incident or kilometres, you'll get an email straight away so nothing slips through.",
    body: [
      { type: "p", text: "Once your email is set up (see the topic above), you'll automatically get a quick alert whenever a worker creates anything that needs your approval — timesheets, vitals, incidents, kilometres or roster changes." },
      { type: "steps", title: "How to turn it on or off", steps: [
        "Open <b>Settings → Email</b>.",
        "At the bottom of the page, find <b>Instant pending-approval alerts</b>.",
        "Toggle it on or off. It's on by default.",
      ]},
      { type: "tip", text: "When you tap an item in <b>Approvals</b> you can approve or reject it in one click. Workers see your decision the next time they open the app." },
    ],
  },
  {
    id: "add-worker", icon: Users, audience: "client",
    title: "Add a support worker (and invite them to register)",
    summary: "Add the worker's details in Support Workers, then share the app with them. They can then register their own account.",
    body: [
      { type: "steps", title: "Add a worker", steps: [
        "Open <b>Support Workers</b> from the sidebar.",
        "Click <b>Add support worker</b>.",
        "Fill in their full name, email, phone, ABN and bank details (these are used on invoices and the cover letter).",
        "Save. They now appear on your roster and timesheets.",
      ]},
      { type: "steps", title: "Invite them to register their own account", steps: [
        "Once you've added their email above, share the app URL with them.",
        "On the login screen they choose the <b>Worker</b> tab and enter the same email plus a password of their choice.",
        "They'll instantly see your account in their Clients list.",
      ]},
      { type: "tip", text: "A worker can be added by multiple clients — they'll see a switcher at the bottom of the sidebar to swap between you." },
      { type: "p", text: "<b>Pay rates</b>: each worker has six rates (weekday day/evening/night, Saturday, Sunday, public holiday). Set these on their profile and timesheets will calculate the right pay automatically." },
    ],
  },
  {
    id: "approve-changes", icon: ClipboardCheck, audience: "client",
    title: "Approve worker changes in your inbox",
    summary: "Anything a worker creates or edits goes pending until you approve it — keeps you in full control.",
    body: [
      { type: "p", text: "Whenever a worker logs a timesheet, vital reading, incident, kilometres or makes a roster change, it shows up in your <b>Approvals</b> page (the bell icon in the sidebar) with a yellow \"Pending\" pill." },
      { type: "steps", title: "How to review and approve", steps: [
        "Open <b>Approvals</b> from the sidebar.",
        "Each row shows what the worker did and a quick summary.",
        "Click <b>Approve</b> — the change becomes live. Or <b>Reject</b> — the change is undone.",
        "Approved items move into your normal lists (Timesheets, Vitals, etc).",
      ]},
      { type: "tip", text: "Once your email is connected, you'll also get an instant email when something is pending — see the \"Instant emails\" topic above." },
    ],
  },
  {
    id: "timesheets", icon: BookOpenCheck, audience: "all",
    title: "Create timesheets — from a worker's shift to the cover letter",
    summary: "Timesheets capture each shift. The right NDIS task code is picked from a dropdown, pay calculates automatically.",
    body: [
      { type: "steps", title: "Create a timesheet", steps: [
        "Open <b>Timesheets</b>.",
        "Click <b>New timesheet</b>.",
        "Pick the worker, the date, and the start/end times.",
        "Choose the <b>task</b> (e.g., Personal care, Community access, Domestic). The matching <b>NDIS code</b> dropdown narrows automatically.",
        "Pick the rate type (weekday day, weekend, public holiday, etc).",
        "Click <b>Save</b>. The duration and pay are calculated for you.",
      ]},
      { type: "p", text: "Workers' timesheets land as <b>Pending</b> until the client approves them. Clients' own entries are auto-approved." },
      { type: "tip", text: "<b>Shift notes</b>: open any timesheet to add care notes. These can be exported as a PDF from the Timesheets page header." },
      { type: "warn", text: "You need an active subscription (or trial) to create or edit timesheets. Read-only access is always free." },
    ],
  },
  {
    id: "invoices", icon: Receipt, audience: "all",
    title: "Generate an invoice and email it to your plan manager",
    summary: "Invoices roll up approved timesheets into a single weekly PDF with a cover letter — ready to email to multiple recipients.",
    body: [
      { type: "steps", title: "Generate", steps: [
        "Open <b>Invoices</b> and click <b>Generate invoice</b>.",
        "Pick the worker and the week. Only approved timesheets in that range are included.",
        "Hit <b>Generate</b> — you'll see a row appear with an auto invoice number like <code>23-02-08-01</code>.",
      ]},
      { type: "steps", title: "Email it", steps: [
        "On any approved invoice row, click the email icon.",
        "The <b>Email recipients</b> dialog shows your plan manager, support workers, medical staff, family and yourself — pick whoever should get a copy.",
        "Type any extra email into the box at the top and press <b>Add</b>.",
        "Click <b>Send</b>. The PDF is attached and sent from your configured email.",
      ]},
      { type: "p", text: "<b>Mark paid</b>: when the plan manager pays, click the green dollar icon to mark it paid — it stops appearing in your unpaid list and Financial Insights." },
      { type: "tip", text: "Need to download the PDF instead of email? Click the PDF button — your browser will let you choose where to save it." },
    ],
  },
  {
    id: "vitals", icon: Heart, audience: "all",
    title: "Log vital signs (BP, pulse, oxygen, weight)",
    summary: "Daily vitals are tracked with a \"recorded by\" field so you always know who took the reading.",
    body: [
      { type: "steps", title: "Add a reading", steps: [
        "Open <b>Vital Signs</b>.",
        "Click <b>Log vitals</b>.",
        "Pick the date and time, then the <b>Recorded by</b> dropdown — choose yourself, the worker on shift, or another carer.",
        "Enter any combination of: blood pressure, pulse, blood glucose, oxygen, weight, temperature.",
        "Save. The reading appears in the table, sorted newest first.",
      ]},
      { type: "tip", text: "Click the email button to send the latest vitals PDF straight to a doctor or the plan manager." },
    ],
  },
  {
    id: "incidents", icon: AlertTriangle, audience: "all",
    title: "Log an incident (with priority levels)",
    summary: "Incidents capture safety events. Priority levels help everyone respond quickly.",
    body: [
      { type: "steps", title: "Log it", steps: [
        "Open <b>Incident Log</b>.",
        "Click <b>Log incident</b>.",
        "Pick the date, location and a short description.",
        "Choose a <b>priority</b>:<br/>• <b>Low</b> — minor, no follow-up needed<br/>• <b>Medium</b> — needs review<br/>• <b>High</b> — urgent, plan manager will be auto-notified",
        "Save. High-priority incidents trigger an email to the plan manager automatically (provided your email is set up).",
      ]},
      { type: "warn", text: "Incidents can never be deleted by workers — only the client can. This protects the audit trail." },
    ],
  },
  {
    id: "kilometres", icon: Car, audience: "all",
    title: "Track kilometres for ATO claims",
    summary: "Log driving time and kilometres — the app calculates the dollar amount at the current ATO cents-per-km rate.",
    body: [
      { type: "steps", title: "Log a trip", steps: [
        "Open <b>Kilometres</b>.",
        "Click <b>Log kilometres</b>.",
        "Pick the date, worker, kilometres driven and a short description.",
        "Save. The dollar amount is calculated using the ATO rate set in <b>Settings → Tax</b>.",
      ]},
      { type: "tip", text: "Email the kilometres PDF to the plan manager at end of month — it's pre-formatted for ATO petrol-claim records." },
    ],
  },
  {
    id: "ndis-budget", icon: Wallet, audience: "client",
    title: "Set your NDIS budget and plan window",
    summary: "Enter your total budget and plan dates so the dashboard can show you a real-time burn-rate projection.",
    body: [
      { type: "steps", title: "Configure", steps: [
        "Open <b>NDIS Budget</b> from the sidebar.",
        "Enter your <b>total plan budget</b> (e.g., $75,000).",
        "Pick the plan <b>start</b> and <b>end</b> dates from your NDIS plan letter.",
        "Save. The dashboard now shows allocated, spent, remaining, % used and a pace projection.",
      ]},
      { type: "tip", text: "The <b>Projection card</b> compares time elapsed vs spend and tells you if you're on track, overspending, or have unused funds. Update it whenever a new plan is approved." },
    ],
  },
  {
    id: "worker-multi", icon: Users, audience: "worker",
    title: "Working for multiple clients — the client switcher",
    summary: "If you support more than one client, use the switcher at the bottom of the sidebar to swap between them. All your data is per-client.",
    body: [
      { type: "p", text: "When a client adds you to their account, you'll see them appear in your <b>My Clients</b> page. The active client is shown at the bottom of the sidebar — click it to switch." },
      { type: "steps", title: "Request to join another client", steps: [
        "Open <b>My Clients</b>.",
        "Click <b>Request to join another client</b>.",
        "Enter their email — they'll get a notification and can approve you.",
        "Once approved, they'll appear in the switcher.",
      ]},
      { type: "tip", text: "Your inbox aggregates pending items across <i>all</i> your clients with per-client tabs so nothing slips through." },
    ],
  },
  {
    id: "worker-inbox", icon: Inbox, audience: "worker",
    title: "Your unified inbox",
    summary: "Everything that needs a client decision plus your upcoming shifts, all in one place.",
    body: [
      { type: "p", text: "Open <b>Inbox</b> to see four sections: Pending timesheets, Pending invoices, Upcoming shifts (next 14 days), and Recent decisions (last 14 days)." },
      { type: "steps", title: "Tabs", steps: [
        "Use the tabs at the top to filter by client.",
        "<b>All</b> shows every engagement together.",
        "<b>Mark all as seen</b> clears the badge in the sidebar — items themselves stay in place.",
      ]},
    ],
  },
  {
    id: "team-chat", icon: MessageCircle, audience: "all",
    title: "Coordinate shifts with team chat",
    summary: "Workers and the client share one chat room per client — perfect for shift swaps and quick questions.",
    body: [
      { type: "p", text: "<b>Team Chat</b> is per-client — you'll only see messages for your active client. Workers can chat with each other and with the client; the client sees everything." },
      { type: "tip", text: "Press <b>Enter</b> to send. The unread badge on the sidebar updates every 15 seconds." },
    ],
  },
  {
    id: "subscription", icon: CreditCard, audience: "all",
    title: "Subscription & billing — what's free, what's paid",
    summary: "Reading and approving is always free. Creating or editing timesheets and invoices needs an active subscription after the trial.",
    body: [
      { type: "list", title: "Free for everyone", items: [
        "Approving worker changes",
        "Reading timesheets, invoices, vitals, incidents, kilometres",
        "AI photos, the Avatar Studio, family gallery",
        "Settings, email setup, team chat",
      ]},
      { type: "list", title: "Needs an active subscription (or trial)", items: [
        "Creating or editing timesheets",
        "Generating or editing invoices",
      ]},
      { type: "p", text: "Every new account gets a <b>30-day free trial</b>. After that, it's $5 AUD per month via Stripe." },
      { type: "steps", title: "Pay", steps: [
        "Open <b>Subscription</b> from the sidebar.",
        "Click <b>Subscribe / Upgrade</b>.",
        "Stripe takes you to a secure checkout page. After payment, you're back in the app with active status.",
      ]},
      { type: "tip", text: "An <b>admin</b> can sponsor any account from the Admin page — useful for trial accounts and demos." },
    ],
  },
  {
    id: "ai-photos", icon: Sparkles, audience: "client",
    title: "AI photos & Avatar Studio",
    summary: "Generate friendly avatar pictures for yourself, workers, family and friends — no photo upload needed.",
    body: [
      { type: "steps", title: "Use it", steps: [
        "Open the worker's profile, family member's card, or <b>Settings → Avatar</b> (your own).",
        "Click <b>AI generate</b>.",
        "Type a friendly prompt, e.g., \"smiling carer in a sunlit room with soft watercolour vibes\".",
        "The image is generated by Gemini's Nano Banana model. Save it to use everywhere across the app.",
      ]},
      { type: "tip", text: "Use <b>AI edit</b> on a real photo to apply a stylised effect (e.g., \"watercolour portrait\"). The original is preserved." },
    ],
  },
  {
    id: "medical-staff", icon: Stethoscope, audience: "all",
    title: "Treating medical staff — keep your care team in one place",
    summary: "Add doctors, OTs, physios and specialists. They're available as recipients when you email PDFs.",
    body: [
      { type: "steps", title: "Add", steps: [
        "Open <b>Medical Staff</b>.",
        "Click <b>Add medical staff</b>.",
        "Fill in name, specialty, phone and email.",
        "Save. They now show up in the <b>Email recipients</b> dialog when sending vitals, incidents and other PDFs.",
      ]},
      { type: "p", text: "You can also link a medical staff member to a <b>Roster</b> appointment so workers know who they're meeting and where." },
    ],
  },
];


// =====================================================================
// Page
// =====================================================================
export default function HelpPage() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(TOPICS[0].id);
  const sectionRefs = useRef({});

  const audience = user?.role === "worker" ? "worker" : "client";

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TOPICS.filter((t) => {
      if (t.audience !== "all" && t.audience !== audience) return false;
      if (!q) return true;
      const hay = (t.title + " " + t.summary + " " + JSON.stringify(t.body)).toLowerCase();
      return hay.includes(q);
    });
  }, [query, audience]);

  // Track which section is in view as the user scrolls
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleIds = entries.filter((e) => e.isIntersecting).map((e) => e.target.id);
        if (visibleIds.length) setActiveId(visibleIds[0]);
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    visible.forEach((t) => {
      const el = sectionRefs.current[t.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [visible]);

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8" data-testid="help-page">
      {/* Sidebar */}
      <aside className="lg:sticky lg:top-4 lg:self-start space-y-3" data-testid="help-sidebar">
        <div className="flex items-center gap-2 mb-1">
          <HelpCircle className="w-5 h-5 text-client" />
          <h2 className="text-lg font-display font-extrabold tracking-tight">Help Center</h2>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search topics…"
            className="pl-9 rounded-full min-h-[44px] border-2"
            data-testid="help-search"
          />
        </div>
        <nav className="space-y-1" data-testid="help-toc">
          {visible.length === 0 && (
            <p className="text-xs text-muted-foreground px-3 py-3">No topics match. Try another keyword.</p>
          )}
          {visible.map((t) => {
            const Icon = t.icon;
            const active = t.id === activeId;
            return (
              <button
                key={t.id}
                onClick={() => scrollTo(t.id)}
                data-testid={`toc-${t.id}`}
                className={`w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-xl transition-all card-lift ${
                  active ? "bg-client/12 ring-1 ring-client/30" : "hover:bg-muted/50"
                }`}
              >
                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${active ? "text-client" : "text-muted-foreground"}`} />
                <span className={`text-sm leading-snug ${active ? "font-bold" : "font-medium"}`}>{t.title}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Articles */}
      <main className="space-y-8 max-w-3xl" data-testid="help-articles">
        <header>
          <Badge variant="secondary" className="rounded-full font-bold mb-3 text-xs uppercase tracking-wider">
            Help Center
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-display font-black tracking-tight leading-tight">
            How to get the most<br />out of your hub
          </h1>
          <p className="text-muted-foreground mt-3 text-base max-w-2xl">
            Step-by-step guides for everything from connecting your email to generating invoices.
            New to the app? Start with <b>Set up your email</b>, then <b>Add a support worker</b>.
          </p>
        </header>

        {visible.length === 0 ? (
          <Card className="p-10 text-center rounded-3xl border-2 border-dashed" data-testid="help-empty">
            <p className="text-muted-foreground">No topics match "<b>{query}</b>". Try a different keyword.</p>
          </Card>
        ) : (
          visible.map((topic) => {
            const Icon = topic.icon;
            return (
              <article
                key={topic.id}
                id={topic.id}
                ref={(el) => { sectionRefs.current[topic.id] = el; }}
                data-testid={`help-topic-${topic.id}`}
                className="scroll-mt-6"
              >
                <Card className="rounded-3xl border-2 p-7 sm:p-8 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-client/15 ring-1 ring-client/25 grid place-items-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-client" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-display font-black tracking-tight leading-tight">
                        {topic.title}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1.5">{topic.summary}</p>
                    </div>
                  </div>
                  {topic.body.map((block, idx) => <Block key={idx} block={block} />)}
                </Card>
              </article>
            );
          })
        )}

        <Card className="rounded-3xl border-2 p-6 bg-client/5">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-client mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold">Still stuck?</p>
              <p className="text-sm text-muted-foreground">
                Use the search bar above, or contact your plan manager for NDIS-specific questions.
                For app questions, get in touch via the email on your subscription receipt.
              </p>
              <p className="text-xs text-muted-foreground mt-3">
                Building an integration?{" "}
                <a href="/api/docs" target="_blank" rel="noopener noreferrer"
                   data-testid="api-docs-link"
                   className="font-bold text-client hover:underline">
                  Browse our API docs →
                </a>
              </p>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}


// =====================================================================
// Block renderers
// =====================================================================
function Block({ block }) {
  if (block.type === "p") {
    return <p className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: block.text }} />;
  }
  if (block.type === "steps") {
    return (
      <div className="space-y-2">
        {block.title && (
          <h3 className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{block.title}</h3>
        )}
        <ol className="space-y-2.5">
          {block.steps.map((step, i) => (
            <li key={i} className="flex gap-3 text-sm leading-relaxed">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-client text-white grid place-items-center text-xs font-black">
                {i + 1}
              </span>
              <span className="flex-1 pt-0.5" dangerouslySetInnerHTML={{ __html: step }} />
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (block.type === "list") {
    return (
      <div className="space-y-2">
        {block.title && (
          <h3 className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{block.title}</h3>
        )}
        <ul className="space-y-1.5">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-2 text-sm leading-relaxed">
              <CheckCircle2 className="w-4 h-4 text-client mt-0.5 flex-shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (block.type === "tip") {
    return (
      <div className="rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200/70 dark:border-amber-700/30 px-4 py-3 flex gap-3">
        <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: `<b>Tip:</b> ${block.text}` }} />
      </div>
    );
  }
  if (block.type === "warn") {
    return (
      <div className="rounded-2xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200/70 dark:border-rose-700/30 px-4 py-3 flex gap-3">
        <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: `<b>Heads up:</b> ${block.text}` }} />
      </div>
    );
  }
  return null;
}
